import {
  CONTRACT_FIELDS,
  contractField,
  onWorksheet,
  settledAnswer,
} from './contract'
import type { ContractField, ContractKind } from './contract'
import { claimBlockers } from './claims'
import {
  CHANGE_TYPES,
  approvals,
  isDriveUrl,
  latestChallenge,
  workType,
} from './decisions'
import type { PolicyDecision, VersionRecord } from './decisions'
import { POLICY_STAGE_LABELS } from './types'
import type { PolicyDocView, PolicyRole, PolicyStage } from './types'
import { definitionFingerprint, scopeFingerprint } from './versions'
import { validateTree } from './validateTree'

/* Every gate returns human-readable blockers; an empty list means allowed.
   Pure, so the page disables a button with exactly the reasons the Durable
   Object would refuse the move with. There is no freezing: every gate below
   either checks the stage directly or checks whether an approval is still
   pinned to the current definition — never a stored "editable" flag. */

const GATED_KINDS: readonly ContractKind[] = ['asked', 'gated', 'open']
/* Rows whose contract marks them needed when they apply: each must be
   answered or marked N/A before scope goes to design. */
const CONFIRM_VALUES: readonly string[] = ['Y', 'Y*', 'If applies', 'As needed']

/** The six fields V1 scoping approval waits on; they can never be N/A (S8). */
export const REQUIRED_FIELD_IDS: readonly string[] = [
  'policy-name',
  'logic-types',
  'topic',
  'intent',
  'description',
  'policy-effective-start-date',
]

const SOURCE_EXCERPTS = 'source-excerpts'

const blocker = (field: ContractField, reason: string): string =>
  `${field.label}: ${reason}`

/** Whether a row is one of the six that must be answered, never N/A. */
export const isRequired = (field: ContractField): boolean =>
  REQUIRED_FIELD_IDS.includes(field.id)

/** Whether a row must be answered or marked N/A before scope goes to design. */
export const needsConfirmation = (field: ContractField): boolean =>
  isRequired(field) ||
  (GATED_KINDS.includes(field.kind) && CONFIRM_VALUES.includes(field.required))

/**
 * Why a row is not yet settled with evidence, or `null` when it is.
 *
 * Settled means a non-empty answer plus at least one citation — a proposal's
 * own citations count, so accepting a sourced proposal settles it. Three
 * exceptions: source excerpts are satisfied by any citation quoting a source;
 * a row with `basis: false` needs only its answer; and an accepted proposal on
 * a row that is not `DERIVED` needs no citation. A `DERIVED` row is read off
 * the source, so accepting an uncited proposal never stands in for one.
 * Basis is optional in V1, so this only informs; it never holds approval.
 */
export const unsettledReason = (
  view: PolicyDocView,
  field: ContractField,
): 'no answer' | 'no citation' | null => {
  if (field.id === SOURCE_EXCERPTS)
    return view.citations.some((citation) => citation.kind === 'source')
      ? null
      : 'no citation'
  if (settledAnswer(field, view.answers[field.id]).muted) return 'no answer'
  if (
    field.basis === false ||
    (field.start !== 'DERIVED' &&
      view.fieldResolutions[field.id] === 'accepted')
  )
    return null
  return view.citations.some((citation) => citation.fieldId === field.id)
    ? null
    : 'no citation'
}

/** Why a row holds the hand-over to design, or `null` when it does not. */
const designHold = (
  view: PolicyDocView,
  field: ContractField,
): string | null => {
  if (!needsConfirmation(field) || !onWorksheet(field, view)) return null
  if (field.id in view.notAsked)
    return isRequired(field) ? 'required, so it can’t be N/A' : null
  if (field.id === SOURCE_EXCERPTS)
    return unsettledReason(view, field) ? 'cite the source or mark N/A' : null
  if (settledAnswer(field, view.answers[field.id]).muted)
    return isRequired(field) ? 'required' : 'confirm or mark N/A'
  /* A value NURO seeded that nobody has reviewed is not confirmed yet; one
     triage or the system seeded came from a person's own choice. */
  if (
    view.fieldResolutions[field.id] === 'proposed' &&
    view.proposals[field.id]?.by === 'neuro'
  )
    return isRequired(field) ? 'confirm the proposed value' : 'confirm or mark N/A'
  return null
}

/**
 * Rows that hold scoping's approval: one of the six required fields left
 * empty or marked N/A, or any other row that applies to this policy and is
 * neither answered nor N/A. Citations never hold it (basis is optional).
 */
export const requiredUnsettled = (view: PolicyDocView): string[] =>
  CONTRACT_FIELDS.flatMap((field) => {
    const reason = designHold(view, field)
    return reason ? [blocker(field, reason)] : []
  })

export const openQuestionBlockers = (view: PolicyDocView): string[] =>
  view.questions
    .filter((question) => question.answer === null)
    .map(
      (question) =>
        `Question on ${contractField(question.fieldId)?.label ?? question.fieldId} unanswered`,
    )

/**
 * The rows the hand-over to design waits on: each required row still
 * unsettled, and each row with an unanswered question. Empty exactly when
 * `approveBlockers(view, decisions, 'scoping')` is.
 */
export const designHoldRows = (view: PolicyDocView): string[] => [
  ...new Set([
    ...CONTRACT_FIELDS.filter((field) => designHold(view, field)).map(
      (field) => field.id,
    ),
    ...view.questions
      .filter((question) => question.answer === null)
      .map((question) => question.fieldId),
  ]),
]

/** Why design cannot hand the tree to validation: a broken tree, an untyped
 *  outcome, unclaimed coverage, or an open question. */
export const designBlockers = (view: PolicyDocView): string[] => [
  ...validateTree(view),
  ...view.insights
    .filter((insight) => !insight.insightType)
    .map((insight) => `Outcome "${insight.label}" has no insight type`),
  ...claimBlockers(view),
  ...view.questions
    .filter((question) => question.closedAt === null)
    .map(
      (question) =>
        `Question on ${contractField(question.fieldId)?.label ?? question.fieldId} not closed`,
    ),
]

/** Why this evidence would not stand behind a decision. The working sheet is
 *  required only when `required` (a new policy's validation approval, V-N4);
 *  anywhere else a link is optional, but must still be a Drive link. */
export const evidenceBlockers = (
  decisions: PolicyDecision[],
  evidenceUrl: string | undefined,
  required = workType(decisions) === 'new',
): string[] => {
  if (!evidenceUrl) return required ? ['Link the working sheet'] : []
  return isDriveUrl(evidenceUrl)
    ? []
    : ['Only a Google Drive link is evidence']
}

/** Why `approve` would be refused for this role, or `[]` when it would not. */
export const approveBlockers = (
  view: PolicyDocView,
  decisions: PolicyDecision[],
  role: PolicyRole,
): string[] => {
  const stage = view.meta.stage
  const label = POLICY_STAGE_LABELS[stage]
  const pendingPublish = stage === 'published' && view.meta.publishedAt === null
  const stageAllowed =
    role === 'scoping'
      ? stage === 'scoping' || stage === 'design' || stage === 'validation'
      : role === 'design'
        ? stage === 'design' || stage === 'validation' || pendingPublish
        : stage === 'validation' || pendingPublish
  if (!stageAllowed)
    return [`Cannot approve ${role} while the policy is ${label}`]

  const roleApprovals = approvals(decisions, view)

  if (role === 'scoping')
    return [...requiredUnsettled(view), ...openQuestionBlockers(view)]

  if (role === 'design') {
    const blockers = [...designBlockers(view)]
    if (!roleApprovals.scoping.current)
      blockers.push(
        'Scoping’s approval no longer counts — scoping must re-approve',
      )
    const challenge = latestChallenge(decisions)
    if (
      challenge &&
      challenge.destination === 'design' &&
      definitionFingerprint(view) === challenge.fingerprint
    )
      blockers.push(
        'Nothing changed since validation sent it back — respond instead',
      )
    return blockers
  }

  const blockers: string[] = []
  if (!roleApprovals.design.current)
    blockers.push('Design’s approval no longer counts — design must re-approve')
  if (!roleApprovals.scoping.current)
    blockers.push(
      'Scoping’s approval no longer counts — scoping must re-approve',
    )
  return blockers
}

/** Why `challenge` would be refused, or `[]` when it would not. */
export const challengeBlockers = (
  view: PolicyDocView,
  decisions: PolicyDecision[],
  input: { explanation: string; evidenceUrl?: string },
): string[] => {
  const stage = view.meta.stage
  const pendingPublish = stage === 'published' && view.meta.publishedAt === null
  if (stage !== 'validation' && !pendingPublish)
    return [
      `Cannot send back while the policy is ${POLICY_STAGE_LABELS[stage]}`,
    ]
  return [
    ...(input.explanation.trim() ? [] : ['Explain what is wrong']),
    ...evidenceBlockers(decisions, input.evidenceUrl, false),
  ]
}

/** The stage a policy returns to when moved back one stage. Awaiting
 *  publish counts as validation; `earlierStages` refuses a finished publish. */
export const PREVIOUS_STAGE: Partial<Record<PolicyStage, PolicyStage>> = {
  design: 'scoping',
  validation: 'design',
  published: 'validation',
}

const REVIEW_ORDER: readonly PolicyStage[] = ['scoping', 'design', 'validation']

/** Stages a policy can be moved or pulled back to from where it is now: any
 *  earlier review stage (D-N16). A policy awaiting publish counts as validation. */
export const earlierStages = (view: PolicyDocView): PolicyStage[] => {
  const stage = view.meta.stage
  const at =
    stage === 'published' && view.meta.publishedAt === null
      ? REVIEW_ORDER.length
      : REVIEW_ORDER.indexOf(stage)
  return at > 0 ? REVIEW_ORDER.slice(0, at) : []
}

/** Why `move-back` to `to` would be refused, or `[]` when it would not. */
export const moveBackBlockers = (
  view: PolicyDocView,
  explanation: string,
  to: PolicyStage | undefined = PREVIOUS_STAGE[view.meta.stage],
): string[] => {
  if (!to || !earlierStages(view).includes(to))
    return [`Cannot move back while the policy is ${POLICY_STAGE_LABELS[view.meta.stage]}`]
  return explanation.trim() ? [] : ['Explain why it is moving back']
}

/** Why `respond` would be refused, or `[]` when it would not. */
export const respondBlockers = (
  view: PolicyDocView,
  decisions: PolicyDecision[],
  explanation: string,
): string[] => {
  const challenge = latestChallenge(decisions)
  if (!challenge || challenge.destination !== view.meta.stage)
    return ['Nothing to respond to']
  const changed =
    view.meta.stage === 'design'
      ? definitionFingerprint(view) !== challenge.fingerprint
      : scopeFingerprint(view) !== challenge.scopeFingerprint
  if (changed)
    return [
      'The definition changed since it was sent back — approve the new version instead',
    ]
  return explanation.trim() ? [] : ['Explain why it stands']
}

/** Why `approve-record` would be refused, or `[]` when it would not. */
export const recordBlockers = (
  view: PolicyDocView,
  decisions: PolicyDecision[],
  record: VersionRecord,
): string[] => {
  const stage = view.meta.stage
  if (!(stage === 'published' && view.meta.publishedAt === null))
    return [
      `Cannot approve the record while the policy is ${POLICY_STAGE_LABELS[stage]}`,
    ]
  const blockers: string[] = []
  if (!approvals(decisions, view).validation.current)
    blockers.push('Validation’s approval no longer counts — validation must re-approve')
  return [...blockers, ...recordFieldBlockers(record)]
}

const recordFieldBlockers = (record: VersionRecord): string[] => [
  ...(record.changeSummary.trim() ? [] : ['Summarize the change']),
  ...((CHANGE_TYPES as readonly string[]).includes(record.changeType)
    ? []
    : ['Pick a change type']),
  ...(record.linesOfBusiness.length ? [] : ['Pick at least one line of business']),
]

/** Whether validation's one-step Publish also records validation's approval:
 *  still in validation, or awaiting publish with that approval gone stale. */
export const publishNeedsApproval = (
  view: PolicyDocView,
  decisions: PolicyDecision[],
): boolean =>
  view.meta.stage === 'validation' || !approvals(decisions, view).validation.current

/**
 * Why validation's one-step Publish (V3) would be refused: approving this
 * version when that is still needed, the record as it will publish, then
 * the publish itself. A failed publish leaves the policy awaiting publish,
 * and the same Publish retries it.
 */
export const publishNowBlockers = (
  view: PolicyDocView,
  decisions: PolicyDecision[],
  record: VersionRecord,
  evidenceUrl: string | undefined,
): string[] => {
  const stage = view.meta.stage
  const pendingPublish = stage === 'published' && view.meta.publishedAt === null
  if (stage !== 'validation' && !pendingPublish)
    return [`Cannot publish while the policy is ${POLICY_STAGE_LABELS[stage]}`]
  if (!publishNeedsApproval(view, decisions)) return recordFieldBlockers(record)
  return [
    ...approveBlockers(view, decisions, 'validation'),
    ...evidenceBlockers(
      decisions,
      evidenceUrl,
      workType(decisions, view.meta) === 'new',
    ),
    ...recordFieldBlockers(record),
  ]
}

/** Why `publish` would be refused, or `[]` when it would not. */
export const publishBlockers = (
  view: PolicyDocView,
  decisions: PolicyDecision[],
): string[] => {
  const stage = view.meta.stage
  if (!(stage === 'published' && view.meta.publishedAt === null))
    return [`Cannot publish while the policy is ${POLICY_STAGE_LABELS[stage]}`]
  const current = approvals(decisions, view)
  const blockers: string[] = []
  if (!current.validation.current)
    blockers.push(
      'Validation’s approval no longer counts — validation must re-approve',
    )
  if (!current.design.current)
    blockers.push('Design’s approval no longer counts — design must re-approve')
  if (!current.scoping.current)
    blockers.push(
      'Scoping’s approval no longer counts — scoping must re-approve',
    )
  /* Approvals are content-addressed, so an unchanged definition keeps last
     cycle's; the record (summary, change type) is the one per-cycle check,
     so it must postdate the last publish or reopen. */
  const cycleStart = Math.max(
    0,
    ...decisions
      .filter((d) => d.decision === 'published' || d.decision === 'reopened')
      .map((d) => d.seq),
  )
  const latestRecord = decisions
    .filter((d) => d.decision === 'record-approved' && d.seq > cycleStart)
    .reduce<PolicyDecision | null>((a, b) => (a && a.seq > b.seq ? a : b), null)
  if (!latestRecord || latestRecord.fingerprint !== definitionFingerprint(view))
    blockers.push('Approve the policy record first')
  return blockers
}
